import type { CSSProperties } from 'react';

/**
 * How a book's story text is laid out. Mirrors app/Support/TextStyle.php —
 * keep the two in step. Nothing saved means DEFAULTS, the original look.
 */
export type TextPosition = 'bottom' | 'top' | 'middle' | 'below' | 'auto';
export type TextBox = 'soft' | 'solid' | 'dark';
export type TextSize = 's' | 'm' | 'l';
export type TextAlign = 'center' | 'start';

export interface TextStyleValue {
    position: TextPosition;
    box: TextBox;
    size: TextSize;
    align: TextAlign;
}

/** What a page actually shows: "auto" is already turned into top or bottom. */
export type ResolvedTextStyle = Omit<TextStyleValue, 'position'> & { position: Exclude<TextPosition, 'auto'> };

/** What the server sends: only the keys that were chosen (PHP sends [] for none). */
export type TextStyleInput = Partial<TextStyleValue> | unknown[] | null | undefined;

export const TEXT_DEFAULTS: TextStyleValue = { position: 'bottom', box: 'soft', size: 'm', align: 'center' };

export const TEXT_OPTIONS = {
    position: [
        { value: 'bottom', label: 'Bottom' },
        { value: 'top', label: 'Top' },
        { value: 'middle', label: 'Middle' },
        { value: 'below', label: 'Under the picture' },
        { value: 'auto', label: 'Auto' },
    ],
    box: [
        { value: 'soft', label: 'Soft' },
        { value: 'solid', label: 'Solid' },
        { value: 'dark', label: 'Dark' },
    ],
    size: [
        { value: 's', label: 'Small' },
        { value: 'm', label: 'Medium' },
        { value: 'l', label: 'Large' },
    ],
    align: [
        { value: 'center', label: 'Centre' },
        { value: 'start', label: 'Start' },
    ],
} as const;

const clean = (s: TextStyleInput): Partial<TextStyleValue> => (s && !Array.isArray(s) ? s : {});

export function bookTextStyle(book: TextStyleInput): TextStyleValue {
    return { ...TEXT_DEFAULTS, ...clean(book) };
}

/** A page's style before "auto" is worked out — what the page panel edits. */
export function pageTextStyle(book: TextStyleInput, page: TextStyleInput): TextStyleValue {
    return { ...bookTextStyle(book), ...clean(page) };
}

export function resolveTextStyle(book: TextStyleInput, page: TextStyleInput, auto?: string | null): ResolvedTextStyle {
    const s = { ...bookTextStyle(book), ...clean(page) };
    const position = s.position === 'auto' ? (auto === 'top' ? 'top' : 'bottom') : s.position;

    return { ...s, position };
}

/** Does this page carry its own style? */
export const hasOwnTextStyle = (page: TextStyleInput) => Object.keys(clean(page)).length > 0;

/** Where the text sits vertically inside the page (a flex container). */
export function textAlignItems(r: ResolvedTextStyle): CSSProperties['alignItems'] {
    return r.position === 'top' ? 'flex-start' : r.position === 'middle' ? 'center' : 'flex-end';
}

const SIZES: Record<TextSize, string> = { s: '13px', m: '15px', l: '18px' };

/**
 * The text box's look on top of a surface's own base style (the viewer and
 * the flipbook each keep their original soft-box colours, so the default
 * style renders exactly as before).
 */
export function textBoxStyle(r: ResolvedTextStyle, base: CSSProperties): CSSProperties {
    const look: CSSProperties =
        r.position === 'below'
            ? { background: 'transparent', color: '#1C1E21', padding: '0 4px' }
            : r.box === 'solid'
              ? { background: '#FFFFFF', color: '#1C1E21' }
              : r.box === 'dark'
                ? { background: 'rgba(28,30,33,0.82)', color: '#FFFFFF' }
                : {};

    return { ...base, ...look, fontSize: SIZES[r.size], textAlign: r.align === 'start' ? 'start' : 'center' };
}

/** Size class, so the phone rules can scale each size (imagerie-styles). */
export const textSizeClass = (r: ResolvedTextStyle) => `ts-${r.size}`;
